import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';

interface Props {
  children: ReactNode;
  // Sheet state — used only at viewports below `lg`, where the inline
  // panel is hidden and the same content slides in from the right
  // instead. Schedule.tsx owns the state so the mobile-only trigger
  // button (in the toolbar) and the sheet stay in sync.
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

// The schedule editor requires a location to be selected at all times
// to avoid activities at different locations visually colliding on the
// same time slot. On `lg+` the panel is rendered inline as a fixed-width
// sidebar so that selection is always one tap away. Below `lg` the same
// content slides in from the right as a sheet — the calendar canvas
// (and the "Add a location" empty state) get the full viewport width,
// and the trigger button in the toolbar carries the active filter count
// so the panel staying hidden by default never feels lossy.
export function FilterDrawer({ children, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  return (
    <>
      <div className="hidden w-[220px] flex-shrink-0 lg:block">
        <div className="h-[calc(100vh-13rem)] overflow-y-auto rounded-xl border border-border bg-card">
          <div className="p-3">{children}</div>
        </div>
      </div>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="flex w-3/4 flex-col gap-3 overflow-y-auto p-4 sm:max-w-sm"
        >
          <SheetTitle className="pr-8 font-heading text-lg font-semibold">
            {t('schedule.filter.title')}
          </SheetTitle>
          {children}
        </SheetContent>
      </Sheet>
    </>
  );
}
